import styled, { keyframes, css } from 'styled-components';

// ── paleta (alinhada ao design system: teal + acentos) ─────────────
const TEAL = 'var(--teal)';
const TEAL_TEXT = 'var(--teal-text, var(--teal))';
const TEAL_LIGHT = 'var(--teal-light)';
const TEAL_SOFT = 'var(--teal-soft)';
const GRAY_BG = 'var(--bg)';
const GRAY_LINE = 'var(--line)';
const GRAY_TEXT = 'var(--text-muted)';
const GREEN = 'var(--green)';
const RED = 'var(--red, #dc2626)';
const AMBER = 'var(--amber, #d97706)';
const CORAL = 'var(--coral)';
const TEAL_GLOW = 'var(--teal-glow)';

const bp = { desktop: '1024px', tablet: '768px', mobile: '540px', small: '380px' };

const expand = keyframes`
  from { opacity: 0; transform: translateY(-6px); max-height: 0; }
  to   { opacity: 1; transform: translateY(0);    max-height: 9999px; }
`;
const fadeIn = keyframes`from { opacity: 0; } to { opacity: 1; }`;
const popIn = keyframes`
  0%   { opacity: 0; transform: scale(0.6); }
  60%  { transform: scale(1.08); }
  100% { opacity: 1; transform: scale(1); }
`;

/* ── Layout base ───────────────────────────────────────────────── */
export const Container = styled.div`
  width: 100%;
  max-width: 100%;
  min-height: 100vh;
  overflow-x: hidden;
  box-sizing: border-box;
  background:
    radial-gradient(1200px 400px at 50% -120px, var(--bg-glow) 0%, transparent 70%),
    ${GRAY_BG};
  display: flex;
  flex-direction: column;
  align-items: center;
`;

export const ContainerDivs = styled.div`
  width: 100%;
  max-width: 960px;
  min-width: 0;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  padding: 16px;
  gap: 14px;
  @media (max-width: ${bp.tablet}) { padding: 12px; }
  @media (max-width: ${bp.mobile}) { padding: 8px; gap: 10px; }
`;

/* ── Cabeçalho da turma (mantém fundo dinâmico via style inline) ── */
export const ClassDetails = styled.div`
  position: relative;
  overflow: hidden;
  box-sizing: border-box;
  width: 100%;
  padding: 22px 24px;
  border-radius: 18px;
  box-shadow: 0 8px 24px ${TEAL_GLOW};
  display: flex;
  flex-direction: column;
  gap: 6px;

  &::before, &::after {
    content: '';
    position: absolute;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.10);
    pointer-events: none;
  }
  &::before { width: 180px; height: 180px; top: -70px; right: -40px; }
  &::after  { width: 120px; height: 120px; bottom: -60px; right: 90px; }

  @media (max-width: ${bp.mobile}) { padding: 18px; border-radius: 16px; }
`;

export const ClassSerie = styled.span`
  position: relative;
  z-index: 1;
  font-size: 24px;
  font-weight: 800;
  letter-spacing: 0.2px;
  @media (max-width: ${bp.mobile}) { font-size: 20px; }
`;

export const ClassInfo = styled.span`
  position: relative;
  z-index: 1;
  font-size: 15px;
  font-weight: 500;
  opacity: 0.92;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  @media (max-width: ${bp.mobile}) { font-size: 14px; }
`;

/* ── Grade de ações ────────────────────────────────────────────── */
export const ButtonContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 12px;
  width: 100%;

  button {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 10px;
    background: var(--surface);
    color: ${TEAL_TEXT};
    padding: 18px 14px;
    border: 1px solid ${GRAY_LINE};
    border-radius: 14px;
    cursor: pointer;
    font-size: 14px;
    font-weight: 600;
    text-align: center;
    line-height: 1.2;
    min-height: 96px;
    transition: transform .14s ease, box-shadow .14s ease, border-color .14s ease;

    svg {
      font-size: 34px;
      flex-shrink: 0;
    }
    &:hover {
      transform: translateY(-3px);
      box-shadow: 0 8px 20px rgba(0,0,0,.08);
      border-color: ${TEAL_LIGHT};
    }
    &:active { transform: translateY(-1px); }
    &:focus-visible { outline: 2px solid ${TEAL_LIGHT}; outline-offset: 2px; }
  }

  /* tablet + mobile: 2 colunas fixas, sem card órfão esticado */
  @media (max-width: ${bp.tablet}) {
    grid-template-columns: repeat(2, 1fr);
    gap: 10px;

    button {
      min-height: 88px;
      padding: 14px 10px;
      font-size: 13px;
      svg { font-size: 30px; }
    }

    /* botão ímpar (Avaliações/Conceitos) ocupa a linha inteira no mobile */
    button.span-mobile {
      grid-column: 1 / -1;
    }
  }

  /* mobile pequeno: cards mais compactos, ainda 2 colunas */
  @media (max-width: ${bp.mobile}) {
    gap: 8px;

    button {
      min-height: 80px;
      padding: 12px 8px;
      gap: 8px;
      font-size: 12px;
      border-radius: 12px;
      svg { font-size: 26px; }
    }
  }

  /* mobile muito pequeno (≤380px): compacta sem espremer o texto */
  @media (max-width: ${bp.small}) {
    gap: 7px;

    button {
      min-height: 74px;
      padding: 10px 6px;
      font-size: 11px;
      svg { font-size: 24px; }
    }
  }
`;

/* ── Diário da turma ───────────────────────────────────────────── */
export const DiaryWrapper = styled.div`
  background: var(--surface);
  border: 1px solid ${GRAY_LINE};
  border-radius: 16px;
  box-shadow: 0 4px 14px rgba(0,0,0,.05);
  box-sizing: border-box;
  width: 100%;
  padding: 18px 20px;

  > h2 {
    color: ${TEAL_TEXT};
    margin: 0 0 14px;
    text-align: center;
    font-size: 18px;
    font-weight: 700;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
  }

  @media (max-width: ${bp.mobile}) { padding: 14px; border-radius: 14px; }
`;

export const DiaryBimester = styled.div`
  margin-bottom: 10px;
  padding: 12px 14px;
  border: 1px solid ${GRAY_LINE};
  border-radius: 12px;
  background: ${GRAY_BG};
  transition: border-color .15s ease, box-shadow .15s ease;
  &:hover { border-color: var(--teal-soft); box-shadow: 0 2px 8px rgba(0,0,0,.04); }
  &:last-child { margin-bottom: 0; }

  h3 {
    margin: 0 0 8px;
    color: ${TEAL_LIGHT};
    font-size: 13px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
`;

export const StatusLine = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 10px;

  strong {
    font-size: 14px;
    color: ${TEAL_TEXT};
    font-weight: 600;
    display: inline-flex;
    align-items: center;
    gap: 8px;
    flex: 1 1 200px;
  }

  button {
    background: ${TEAL};
    color: #fff;
    border: none;
    padding: 8px 16px;
    border-radius: 8px;
    cursor: pointer;
    font-size: 13px;
    font-weight: 600;
    flex-shrink: 0;
    min-height: 38px;
    transition: filter .12s ease, transform .12s ease;
    &:hover { filter: brightness(1.1); }
    &:active { transform: translateY(1px); }
    &:focus-visible { outline: 2px solid ${TEAL_LIGHT}; outline-offset: 2px; }

    @media (max-width: ${bp.mobile}) { width: 100%; min-height: 42px; }
  }
`;

/* pílula de status aberto/fechado */
export const StatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 12px;
  font-weight: 700;
  padding: 3px 10px;
  border-radius: 999px;
  letter-spacing: 0.2px;
  white-space: nowrap;
  ${({ $open }) => $open
    ? css`background: var(--green-soft); color: ${GREEN};`
    : css`background: var(--red-soft); color: ${CORAL};`}
`;

/* aviso de prazo vencido — em vermelho, tipo alerta */
export const DeadlineWarning = styled.div`
  margin: 0 0 10px;
  padding: 10px 12px;
  border: 1px solid ${RED};
  border-left: 4px solid ${RED};
  border-radius: 8px;
  background: var(--red-soft);
  color: ${RED};
  font-size: 12.5px;
  font-weight: 600;
  line-height: 1.4;
  display: flex;
  align-items: flex-start;
  gap: 8px;
  animation: ${fadeIn} .2s ease;

  span[role="img"] { font-size: 15px; line-height: 1.2; flex-shrink: 0; }
`;

/* botão "Ver Diário" — variante secundária */
export const SeeDiaryButton = styled.button`
  background: ${TEAL_SOFT} !important;
  color: ${TEAL_TEXT} !important;
  border: 1px solid var(--teal-soft) !important;
  &:hover { background: var(--teal-soft) !important; filter: none !important; }
`;

/* bloco resultado final em destaque */
export const FinalResultBlock = styled.div`
  margin-top: 10px;
  padding: 14px;
  border: 1px solid var(--green);
  border-radius: 12px;
  background: var(--green-soft);
  display: flex;
  flex-direction: column;
  gap: 10px;

  h3 {
    margin: 0;
    color: ${GREEN};
    font-size: 13px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  button {
    background: ${GREEN};
    color: #fff;
    border: none;
    padding: 10px 16px;
    border-radius: 8px;
    cursor: pointer;
    font-weight: 700;
    font-size: 14px;
    min-height: 40px;
    transition: filter .12s ease, transform .12s ease;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    &:hover { filter: brightness(1.08); }
    &:active { transform: translateY(1px); }
  }
`;

/* ── Seção de alunos ───────────────────────────────────────────── */
export const StudentSection = styled.div`
  background: var(--surface);
  border: 1px solid ${GRAY_LINE};
  border-radius: 16px;
  box-shadow: 0 4px 14px rgba(0,0,0,.05);
  box-sizing: border-box;
  width: 100%;
  padding: 18px 20px;

  > h2 {
    color: ${TEAL_TEXT};
    margin: 0 0 12px;
    font-size: 18px;
    font-weight: 700;
    display: flex;
    align-items: center;
    gap: 8px;
  }

  @media (max-width: ${bp.mobile}) { padding: 14px; border-radius: 14px; }
`;

/* barra de ações (imprimir / emitir) */
export const StudentActions = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-bottom: 14px;
`;

export const LinkAction = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: ${TEAL_SOFT};
  color: ${TEAL_TEXT};
  border: 1px solid var(--teal-soft);
  padding: 9px 14px;
  border-radius: 9px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  min-height: 38px;
  transition: background .12s ease, transform .12s ease;
  &:hover { background: var(--teal-soft); }
  &:active { transform: translateY(1px); }
  &:focus-visible { outline: 2px solid ${TEAL_LIGHT}; outline-offset: 2px; }

  @media (max-width: ${bp.mobile}) { flex: 1 1 100%; justify-content: center; min-height: 42px; }
`;

/* faixa de contadores */
export const CountRow = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-bottom: 12px;
`;

export const CountChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 600;
  color: ${GRAY_TEXT};
  background: ${GRAY_BG};
  border: 1px solid ${GRAY_LINE};
  border-radius: 999px;
  padding: 5px 12px;

  strong { color: ${TEAL_TEXT}; font-weight: 700; }
`;

export const TransferHeading = styled.div`
  margin: 16px 0 8px;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: ${AMBER};
  display: flex;
  align-items: center;
  gap: 8px;
  padding-top: 12px;
  border-top: 1px dashed ${GRAY_LINE};
`;

export const StudentItem = styled.li`
  list-style: none;
  font-size: 15px;
  color: ${TEAL_TEXT};
  font-weight: 500;
  padding: 12px 14px;
  border: 1px solid ${GRAY_LINE};
  border-radius: 10px;
  margin-bottom: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  transition: border-color .14s ease, transform .14s ease, box-shadow .14s ease;
  animation: ${popIn} .25s ease backwards;

  &:hover {
    border-color: ${TEAL_LIGHT};
    transform: translateX(2px);
    box-shadow: 0 2px 8px rgba(0,0,0,.05);
  }

  @media (max-width: ${bp.mobile}) { font-size: 14px; padding: 11px 12px; }
  @media (max-width: ${bp.small}) { font-size: 13px; }
`;

/* pílula de status do aluno */
export const StudentStatus = styled.span`
  font-size: 11px;
  font-weight: 700;
  padding: 3px 10px;
  border-radius: 999px;
  text-transform: capitalize;
  white-space: nowrap;
  ${({ $status }) => {
    switch ($status) {
      case 'ativo':       return css`background:var(--green-soft); color:${GREEN};`;
      case 'inativo':     return css`background:var(--red-soft); color:${RED};`;
      case 'transferido': return css`background:rgba(217,119,6,0.18); color:${AMBER};`;
      case 'remanejado':  return css`background:rgba(59,130,246,0.18); color:var(--blue, #3b82f6);`;
      default:            return css`background:var(--surface-2); color:${GRAY_TEXT};`;
    }
  }}
`;

export const InfoText = styled.div`
  font-size: 14px;
  color: ${GRAY_TEXT};
  padding: 20px 0;
  text-align: center;
`;

/* ── Voltar ────────────────────────────────────────────────────── */
export const ToGoBack = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  margin: 8px 0 16px;
  padding: 12px;
  cursor: pointer;
  color: ${GRAY_TEXT};
  font-size: 14px;
  min-height: 44px;
  border-radius: 10px;
  transition: color .15s ease, background .15s ease;
  &:hover { color: ${TEAL_TEXT}; background: ${TEAL_SOFT}; }

  @media print { display: none; }
`;

export const SignMessageButtonText = styled.span`
  color: inherit;
  @media (max-width: ${bp.mobile}) { font-size: 14px; }
`;

export const SignMessageButtonTextBold = styled.span`
  color: ${TEAL_TEXT};
  font-weight: 700;
  margin-left: 5px;
  @media (max-width: ${bp.mobile}) { font-size: 14px; }
`;

/* mantido por compat (não mais usado diretamente) */
export const AddImpre = styled.div`
  display: flex;
  justify-content: flex-end;
`;

/* ── Modais ────────────────────────────────────────────────────── */
const overlayBase = css`
  position: fixed;
  inset: 0;
  backdrop-filter: blur(4px);
  background-color: rgba(0, 30, 38, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 16px;
  animation: ${fadeIn} .15s ease;
  @media (max-width: ${bp.mobile}) { padding: 0; align-items: flex-end; }
`;

export const ContainerModal = styled.div`${overlayBase}`;
export const ModalBackground = styled.div`${overlayBase}`;

const modalCardBase = css`
  background: var(--surface);
  padding: 24px;
  border-radius: 16px;
  max-width: 420px;
  width: 100%;
  box-shadow: 0 18px 50px rgba(0,0,0,0.25);
  text-align: center;
  animation: ${expand} .2s ease;

  h2 {
    margin: 0 0 16px;
    color: ${TEAL_TEXT};
    font-size: 18px;
    font-weight: 700;
  }
  > p {
    color: ${GRAY_TEXT};
    font-size: 14px;
    line-height: 1.55;
    margin: 0 0 8px;
  }

  @media (max-width: ${bp.mobile}) {
    border-radius: 18px 18px 0 0;
    padding: 22px 18px;
    max-width: 100%;
  }
`;

export const ModalContent = styled.div`${modalCardBase}`;
export const ModalContainer = styled.div`${modalCardBase}`;

export const Input = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  text-align: left;
`;

export const Label = styled.label`
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  color: ${GRAY_TEXT};
`;

export const Select = styled.select`
  border: 1px solid ${GRAY_LINE};
  background: var(--surface);
  border-radius: 9px;
  padding: 11px 12px;
  font-size: 14px;
  color: ${TEAL_TEXT};
  outline: none;
  cursor: pointer;
  min-height: 44px;
  transition: border-color .15s ease, box-shadow .15s ease;
  &:focus { border-color: ${TEAL_LIGHT}; box-shadow: 0 0 0 3px rgba(10,90,115,.12); }
  @media (max-width: ${bp.mobile}) { font-size: 16px; }
`;

export const ModalButton = styled.button`
  margin-top: 20px;
  padding: 11px 22px;
  border: none;
  background: ${TEAL};
  color: #fff;
  border-radius: 9px;
  cursor: pointer;
  font-weight: 700;
  font-size: 14px;
  min-height: 42px;
  transition: filter .12s ease, transform .12s ease;
  &:hover { filter: brightness(1.1); }
  &:active { transform: translateY(1px); }
`;

/* botões genéricos de modal (confirm/cancel) */
export const ModalActions = styled.div`
  display: flex;
  gap: 10px;
  justify-content: center;
  margin-top: 22px;
  @media (max-width: ${bp.small}) {
    flex-direction: column-reverse;
    & > button { width: 100%; }
  }
`;

export const ModalConfirm = styled.button`
  background: ${TEAL};
  color: #fff;
  border: none;
  padding: 11px 20px;
  border-radius: 9px;
  cursor: pointer;
  font-weight: 700;
  font-size: 14px;
  min-height: 42px;
  transition: filter .12s ease, transform .12s ease;
  &:hover { filter: brightness(1.1); }
  &:active { transform: translateY(1px); }
`;

export const ModalCancel = styled.button`
  background: var(--surface-2);
  color: ${GRAY_TEXT};
  border: 1px solid ${GRAY_LINE};
  padding: 11px 20px;
  border-radius: 9px;
  cursor: pointer;
  font-weight: 600;
  font-size: 14px;
  min-height: 42px;
  transition: background .12s ease;
  &:hover { background: var(--surface-2); }
`;

export const ModalWarnText = styled.p`
  color: ${RED} !important;
  font-weight: 700 !important;
  font-size: 13px !important;
  margin-top: 10px !important;
`;